import * as THREE from 'three';
import type { BattleState, Enemy } from '@somnhold/sim';
import type { UnCtx, UnLook } from './ctx.ts';

/*
 * What every Unsleeping's moments share, in his colours: his presence, arriving, the channel starting, cut, a new
 * phase, fading into smoke (caught or not), gone, and felled for good.
 */

const TAU = Math.PI * 2;
const v = new THREE.Vector3();

/**
 * Every frame: his light on the ground, sparks off his cracks, smoke off his mantle; channelling, power drawn into him;
 * fading, smoke pouring off him and a ring closing on him as the time to hold him runs out.
 */
export const presence = (c: UnCtx, s: BattleState, e: Enemy, p: THREE.Vector3, h: number, look: UnLook, fresh: boolean, glow: THREE.Color): void => {
  const u = e.un;
  if (!u) return;
  c.glow(p.clone().setY(p.y + 0.06), glow, u.cast > 0 || u.flee > 0 ? 2.6 : 1.6, u.cast > 0 || u.flee > 0 ? 0.45 : 0.22);
  if (!fresh) return;
  if (u.flee > 0) {
    // He comes apart: thick smoke off his whole body, his light flickering out, a pale ring tightening each beat.
    const beat = (s.tick + e.id) % 4;
    c.vfx.burst(p.clone().setY(p.y + h * 0.5), { n: c.low ? 2 : 5, color: look.dark, speed: 0.02, up: 0.07, size: 0.9, life: 26, smoke: true, spread: 0.55 });
    c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: c.low ? 1 : 3, color: look.glow, speed: 0.03, up: 0.05, size: 0.08, life: 16, gravity: -0.004, spread: 0.4 });
    if (beat === 0) c.vfx.ring(p.clone().setY(p.y + 0.08), 2.8, 0.4, look.pale, 12, 0.9);
    c.flare(p.clone().setY(p.y + h * 0.6), look.pale, 1.4 + (s.tick % 2) * 0.8, 2);
    c.shake(0.05);
    return;
  }
  const beat = (s.tick + e.id) % 12;
  if (beat % 3 === 0) {
    const a = Math.random() * TAU;
    c.vfx.burst(p.clone().add(v.set(Math.cos(a) * 0.3, h * (0.3 + Math.random() * 0.45), Math.sin(a) * 0.3)), { n: 1, color: look.glow, speed: 0.01, up: 0, size: 0.09, life: 9, gravity: -0.006 });
  }
  if (!c.low && beat % 4 === 0) c.vfx.burst(p.clone().setY(p.y + h * 0.7), { n: 1, color: look.smoke, speed: 0.01, up: 0.03, size: 0.55, life: 18, smoke: true, spread: 0.45 });
  if (u.cast > 0) {
    const top = p.clone().setY(p.y + h * 1.25);
    if (beat % 2 === 0) c.vfx.implode(top, 1.4, { n: c.low ? 3 : 6, color: look.pale, size: 0.1, life: 10, swirl: 0.4, lift: 0.02 });
    c.flare(top, look.accent, 1.2 + 1.6 * (1 - u.cast / Math.max(1, u.castLen)), 2);
    c.shake(0.04);
  }
};

/** His shared moments by name (`Arrive`, `Broken`, …); false if `name` isn't one. `ult` is his channel's start. */
export const moment = (c: UnCtx, name: string, p: THREE.Vector3, h: number, look: UnLook, ult: boolean): boolean => {
  const low = c.low;
  if (ult) {
    c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 1.8, look.accent, 14, 1);
    c.flare(p.clone().setY(p.y + h * 1.2), look.glow, 3, 12);
    c.vfx.burst(p.clone().setY(p.y + h * 1.2), { n: low ? 8 : 20, color: look.pale, speed: 0.06, up: 0.06, size: 0.1, life: 14, gravity: 0.004 });
    c.shake(0.2);
    return true;
  }
  switch (name) {
    case 'Arrive':
      // The ground cracks open with light, smoke pours up, and he stands in it.
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 3.6, look.accent, 22, 1);
      c.vfx.ring(p.clone().setY(p.y + 0.08), 0.1, 2.2, look.pale, 16, 0.8);
      c.vfx.shaft(p, p.clone().setY(p.y + 6), look.accent, 18, 0.9);
      c.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 12 : 34, color: look.dark, speed: 0.06, up: 0.08, size: 1.0, life: 40, smoke: true, spread: 0.9 });
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 14 : 40, color: look.glow, speed: 0.09, up: 0.14, size: 0.14, life: 24, gravity: 0.006, spread: 0.6 });
      c.flare(p.clone().setY(p.y + h * 0.6), look.accent, 5, 20);
      c.shake(0.7);
      return true;
    case 'Broken':
      // Cut: his glass cracks and flies off in shards, a pale light where it broke, and he drops to a knee.
      c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: low ? 16 : 44, color: '#2c2f3a', speed: 0.14, up: 0.1, size: 0.16, life: 22, gravity: 0.014, spread: 0.35 });
      c.vfx.burst(p.clone().setY(p.y + h * 0.6), { n: low ? 8 : 22, color: '#dfe8ff', speed: 0.12, up: 0.08, size: 0.08, life: 16, gravity: 0.01, spread: 0.3 });
      c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, 2.6, '#dfe8ff', 12, 1);
      c.flare(p.clone().setY(p.y + h * 0.6), '#ffffff', 4.5, 10);
      c.shake(0.55);
      return true;
    case 'Phase':
      // A piece of his armour bursts off; the cracks burn brighter.
      c.vfx.burst(p.clone().setY(p.y + h * 0.7), { n: low ? 10 : 26, color: '#23252e', speed: 0.12, up: 0.1, size: 0.18, life: 20, gravity: 0.014, spread: 0.3 });
      c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, 3, look.accent, 16, 1);
      c.flare(p.clone().setY(p.y + h * 0.7), look.accent, 4, 14);
      c.shake(0.4);
      return true;
    case 'Flee':
      // He starts to come apart into smoke: a crack of pale light, a wide ring, the smoke bursting off him.
      c.vfx.burst(p.clone().setY(p.y + h * 0.5), { n: low ? 14 : 36, color: look.dark, speed: 0.05, up: 0.08, size: 1.1, life: 34, smoke: true, spread: 0.7 });
      c.vfx.implode(p.clone().setY(p.y + h * 0.5), 2.6, { n: low ? 8 : 20, color: look.glow, size: 0.1, life: 18, swirl: 0.6, lift: 0.04 });
      c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, 3.4, look.pale, 16, 1);
      c.vfx.shaft(p, p.clone().setY(p.y + 4), look.dark, 24, 0.7);
      c.flare(p.clone().setY(p.y + h * 0.6), look.pale, 4, 12);
      c.shake(0.35);
      return true;
    case 'Held':
      // A blow caught him before he faded: the smoke snaps back into glass.
      c.vfx.ring(p.clone().setY(p.y + 0.1), 1.6, 0.3, look.pale, 10, 1);
      c.flare(p.clone().setY(p.y + h * 0.6), look.pale, 2.5, 8);
      return true;
    default:
      return false;
  }
};

/** Gone into smoke: a column of it rising, his light going out. */
export const vanished = (c: UnCtx, p: THREE.Vector3, look: UnLook): void => {
  const low = c.low;
  c.vfx.shaft(p, p.clone().setY(p.y + 5), '#22232b', 22, 0.8);
  c.vfx.burst(p.clone().setY(p.y + 0.8), { n: low ? 14 : 36, color: look.dark, speed: 0.03, up: 0.12, size: 1.1, life: 44, smoke: true, spread: 0.5 });
  c.vfx.burst(p.clone().setY(p.y + 1), { n: low ? 6 : 16, color: look.glow, speed: 0.04, up: 0.1, size: 0.08, life: 24, gravity: -0.002 });
  c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, 2.2, '#3a3a46', 16, 0.8);
};

/** Felled for good: the glass bursts apart in shards and light, and for a moment the guardian rises pale out of it. */
export const felled = (c: UnCtx, p: THREE.Vector3, look: UnLook): void => {
  const low = c.low;
  c.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 24 : 70, color: '#272a34', speed: 0.16, up: 0.12, size: 0.2, life: 30, gravity: 0.012, spread: 0.5 });
  c.vfx.burst(p.clone().setY(p.y + 1.2), { n: low ? 16 : 50, color: look.glow, speed: 0.12, up: 0.12, size: 0.1, life: 26, gravity: 0.006, spread: 0.4 });
  c.vfx.burst(p.clone().setY(p.y + 1.6), { n: low ? 6 : 16, color: '#dfe8ff', speed: 0.02, up: 0.05, size: 0.5, life: 50, gravity: -0.004, spread: 0.2 });
  c.vfx.shaft(p, p.clone().setY(p.y + 7), '#dfe8ff', 34, 0.6);
  c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, 5, '#dfe8ff', 24, 1);
  c.vfx.ring(p.clone().setY(p.y + 0.08), 0.2, 3, look.accent, 18, 1);
  c.flare(p.clone().setY(p.y + 2), '#ffffff', 8, 30);
  c.shake(1);
};
